import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnderaiterMark, LogoIcon } from '@/components/brand/Marks';
import {
  ArrowRight, Zap, Brain, FileCheck, FileText,
  Globe, Lock, CheckCircle, XCircle, Play
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
import DemoPlayer from '@/components/DemoPlayer';
import { ShareButtons, watchUrl } from '@/components/ShareDemo';
import { TAGLINE } from '@/components/brand/Marks';
import { PLATFORM_DEMO } from './DemoPage';
const NAVY = '#0D1B2A';
const BLUE = '#1A4C7C';
const TEAL = '#00B4D8';
const GREEN = '#1D9E75';
const ORANGE = '#FF9933';

// Stat value — final value renders immediately (no zero state); true numbers also live in static HTML meta/OG for crawlers
function Counter({ end, suffix = '', prefix = '' }) {
  return <span>{prefix}{end.toLocaleString()}{suffix}</span>;
}

// Fade-up on scroll
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.15 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
    }}>{children}</div>
  );
}

// Live scoring animation — dark terminal (intentional contrast anchor). Loops: scores, holds the
// decision for a few seconds, then runs again — a visitor never meets a dead frame.
const DEMO_STAGES = [
  { label: 'Entity matched', dim: 'Database', color: BLUE },
  { label: 'Claims history analysed', dim: 'Dimension G', color: GREEN },
  { label: 'Hazard profile scored', dim: 'Dimension D', color: '#E8A020' },
  { label: 'Financial health checked', dim: 'Dimension F', color: GREEN },
  { label: 'Industry risk assessed', dim: 'Dimension B', color: GREEN },
  { label: 'Declaration consistency checked', dim: 'Consistency', color: TEAL },
  { label: 'AI narrative generated', dim: 'Claude', color: TEAL },
];
const DEMO_STEP_MS = 450;
const DEMO_HOLD_MS = 4500;
const DEMO_SCORE = 1847;

function ScoringDemo() {
  const [stage, setStage] = useState(0);
  const [cycle, setCycle] = useState(0);
  const n = DEMO_STAGES.length;

  useEffect(() => {
    setStage(0);
    let i = 0;
    let hold;
    const timer = setInterval(() => {
      i += 1;
      setStage(i);
      if (i >= n) {
        clearInterval(timer);
        hold = setTimeout(() => setCycle(c => c + 1), DEMO_HOLD_MS);
      }
    }, DEMO_STEP_MS);
    return () => { clearInterval(timer); clearTimeout(hold); };
  }, [cycle, n]);

  const isComplete = stage >= n;
  const score = isComplete ? DEMO_SCORE : Math.floor((stage / n) * DEMO_SCORE);

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-2xl" style={{ background: NAVY, border: `1px solid ${BLUE}` }} data-testid="scoring-demo" data-cycle={cycle} data-complete={isComplete ? 'true' : 'false'}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
        </div>
        <span className="text-[10px] font-mono ml-2" style={{ color: '#D8DEE6' }}>scoring-engine v1.0</span>
        <span className="ml-auto flex items-center gap-1.5 text-[10px] font-mono" style={{ color: isComplete ? '#8FA3B8' : TEAL }}>
          <span className={`w-1.5 h-1.5 rounded-full ${isComplete ? '' : 'animate-pulse'}`} style={{ background: isComplete ? '#8FA3B8' : TEAL }} />
          {isComplete ? 'replays in a moment' : 'scoring live'}
        </span>
        <button type="button" onClick={() => setCycle(c => c + 1)} className="ml-2 text-[10px] font-mono px-2 py-0.5 rounded transition-colors hover:bg-white/10" style={{ color: '#D8DEE6', border: '1px solid rgba(255,255,255,0.15)' }} data-testid="demo-replay" aria-label="Replay the scoring demo">
          ↻ replay
        </button>
      </div>

      <div className="p-5 font-mono text-xs space-y-2" style={{ minHeight: 318 }}>
        {DEMO_STAGES.slice(0, Math.min(stage, n)).map((s, i) => (
          <div key={`${cycle}-${s.dim}`} className="flex items-center gap-2 animate-fadeIn" style={{ animationDelay: `${i * 40}ms` }}>
            <CheckCircle className="w-3.5 h-3.5 shrink-0" style={{ color: s.color }} />
            <span style={{ color: '#D8DEE4' }}>{s.label}</span>
            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded" style={{ background: s.color + '20', color: s.color }}>{s.dim}</span>
          </div>
        ))}
        {stage === 0 && (
          <div className="flex items-center gap-2" style={{ color: '#8FA3B8' }}>
            <span className="w-3.5 h-3.5 shrink-0 rounded-full border border-current animate-spin" style={{ borderTopColor: 'transparent' }} />
            <span>Sydney cafe · public liability · broker email received</span>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span style={{ color: '#D8DEE6' }}>COMPOSITE SCORE</span>
            <span className="text-lg font-bold" style={{ color: isComplete ? GREEN : TEAL }}>
              {score}<span className="text-xs font-normal" style={{ color: '#D8DEE6' }}>/10,000</span>
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${(score / 10000) * 100}%`, background: isComplete ? GREEN : TEAL }} />
          </div>
        </div>

        {isComplete && (
          <div className="mt-3 p-3 rounded-lg animate-fadeIn" style={{ background: 'rgba(29,158,117,0.15)', border: '1px solid rgba(29,158,117,0.3)' }} data-testid="demo-decision">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" style={{ color: GREEN }} />
              <span className="font-bold" style={{ color: GREEN }}>DECISION: APPROVE</span>
              <span className="ml-auto text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(0,180,216,0.2)', color: TEAL }}>STP</span>
            </div>
            <p className="mt-1.5 text-[10px]" style={{ color: '#D8DEE6' }}>Policy schedule, certificate and tax invoice generated in 4.2 seconds</p>
          </div>
        )}
      </div>
      {/* Cycle progress — the hold before the next run */}
      <div className="h-0.5 w-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
        {isComplete
          ? <div key={cycle} className="h-full" style={{ background: GREEN, animation: `demoHold ${DEMO_HOLD_MS}ms linear forwards` }} />
          : <div className="h-full transition-all duration-300" style={{ background: TEAL, width: `${(stage / n) * 100}%` }} />}
      </div>
    </div>
  );
}

// "Watch the 2-minute demo" — the owner's recording when one is set; the live scoring run until then.
function DemoVideoModal({ open, onClose, embed, onRequestDemo }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  const kind = embed?.kind;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8" style={{ background: 'rgba(13,27,42,0.82)', backdropFilter: 'blur(6px)' }} onClick={onClose} data-testid="demo-video-modal" role="dialog" aria-modal="true" aria-label="underAIter demo">
      <div className="relative w-full max-w-4xl" onClick={e => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute -top-10 right-0 text-sm font-medium text-white/80 hover:text-white transition-colors" data-testid="demo-video-close">Close ✕</button>
        {kind === 'mp4' ? (
          <video src={embed.src} controls autoPlay playsInline className="w-full rounded-2xl shadow-2xl bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="demo-video-player" />
        ) : kind ? (
          <div className="w-full rounded-2xl overflow-hidden shadow-2xl bg-black" style={{ aspectRatio: '16 / 9' }}>
            <iframe src={embed.src} title="underAIter demo" className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="demo-video-iframe" />
          </div>
        ) : (
          <div data-testid="demo-video-placeholder">
            <DemoPlayer slides={PLATFORM_DEMO.slides} totalDuration={PLATFORM_DEMO.totalDuration} audioSrc={PLATFORM_DEMO.audioSrc} accentColor={TEAL} browserTitle="underaiter.com" posterImage={PLATFORM_DEMO.poster} embedded autoPlay />
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-white">
              <p className="text-sm" style={{ color: '#B0BEC5' }}>Eleven screens, under three minutes — intake, scoring, claims, renewals, compliance and the month-end close.</p>
              <div className="flex flex-wrap items-center gap-2">
                <ShareButtons url={watchUrl()} compact />
                <button type="button" onClick={onRequestDemo} className="px-5 py-2.5 rounded-full text-sm font-bold text-white transition-all duration-300 hover:shadow-xl hover:scale-[1.02]" style={{ background: ORANGE }} data-testid="demo-video-request">
                  Request a live demo <ArrowRight className="w-4 h-4 inline ml-1.5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: Brain, title: '529-Variable Scoring', desc: '21 weighted dimensions across 10,000 points. Claude generates risk narratives. Override rules enforce mandatory decisions.' },
  { icon: FileCheck, title: 'Declaration Consistency', desc: 'Broker declarations are checked for internal consistency against the evidence provided. Differences are surfaced to the underwriter and noted on the record.' },
  { icon: Zap, title: 'Straight-Through Processing', desc: 'Email received to decision reply in minutes. A measured 26% of submissions decide straight through today — a figure we publish, not project — each with a full audit trail.' },
  { icon: FileText, title: '18 Insurance Classes', desc: 'SME Commercial, Public Liability, Motor, Motor Trade, Landlords, Strata, PI, Farm, Construction, Trade Credit and Cyber — five fully calibrated, with further classes calibrating through 2027.' },
  { icon: Globe, title: '302,000+ Entities', desc: '302,000+ pre-enriched AU and NZ businesses. Hazard profiles, financial indicators, claims history — scored before the broker submits.' },
  { icon: Lock, title: 'Built for AFSL obligations', desc: '15 regulatory modules. Immutable audit trail. AI transparency. Every decision explainable in plain English. Licensing obligations remain with the licensee.' },
];

const REPLACEMENTS = [
  { from: 'Email inbox for submissions', to: 'Automated email intake pipeline' },
  { from: 'Spreadsheet underwriting register', to: 'Live searchable policy register' },
  { from: 'Manual policy schedules', to: 'Auto-generated in 60 seconds' },
  { from: 'Claims spreadsheet', to: 'FNOL to closure, fully managed' },
  { from: 'Reinsurance accumulation Excel', to: 'Live treaty management with hard stops' },
  { from: 'Broker relationship management', to: 'AI-powered broker onboarding' },
];

const DAY_IN_LIFE = [
  { n: '1', title: 'A risk arrives', desc: "A Sydney cafe wants business cover. Their broker submits it through the portal — or just emails it in.", replaces: 'A shared inbox and a re-keyed spreadsheet' },
  { n: '2', title: 'Scored in seconds', desc: "The engine checks appetite, occupation, hazards, financials and claims history — 21 dimensions, one score.", replaces: 'A separate rating spreadsheet and gut feel' },
  { n: '3', title: 'Clean risks bind themselves', desc: "A clean cafe auto-quotes and binds straight through. A messy one lands on the underwriter's exception queue — humans only see the hard cases.", replaces: 'Days of email back-and-forth' },
  { n: '4', title: 'Paperwork does itself', desc: "Policy schedule, certificate, tax invoice and broker commission are all generated the moment the policy binds.", replaces: 'Manual schedules and an accounts inbox' },
  { n: '5', title: 'The rest runs on autopilot', desc: "Renewals are offered, claims are triaged and the monthly finance close runs in the background. You review exceptions — not queues.", replaces: 'A renewal diary, claims spreadsheet and month-end Excel' },
];

const STEPS = [
  { step: '1', title: 'Submit', desc: 'Broker submits a risk via portal or email. Entity auto-matched from 302K pre-enriched businesses.' },
  { step: '2', title: 'Score', desc: 'AI scores across 21 dimensions in under 5 seconds. Claude writes the risk narrative.' },
  { step: '3', title: 'Bind', desc: 'Approved submissions bind instantly. Policy schedule and certificate generated automatically.' },
  { step: '4', title: 'Manage', desc: 'Claims, renewals, compliance, and finance — all from the same policy data. One platform.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [liveStats, setLiveStats] = useState({ entities: 302575, classes: 18, stp_measured_pct: null });
  const [site, setSite] = useState({ tagline: TAGLINE, demo_video: { kind: null, src: null } });
  const [videoOpen, setVideoOpen] = useState(false);

  useEffect(() => {
    // Cached server-side (≤1h stale) — always sub-second for visitors
    fetch(`${BACKEND}/api/public/stats`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setLiveStats(prev => ({ ...prev, ...d }));
    }).catch(err => { console.error('API error:', err?.message); });
    fetch(`${BACKEND}/api/public/site`).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setSite(prev => ({ ...prev, ...d }));
    }).catch(err => { console.error('API error:', err?.message); });
  }, []);

  const handleGetStarted = () => navigate('/login?mode=waitlist');
  const closeVideo = () => setVideoOpen(false);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <DemoVideoModal open={videoOpen} onClose={closeVideo} embed={site.demo_video} onRequestDemo={() => { setVideoOpen(false); handleGetStarted(); }} />
      {/* ─── Nav ─── */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.85)', borderBottom: '1px solid rgba(226,232,240,0.7)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="nav-brand">
            <LogoIcon size={40} />
            <UnderaiterMark size="text-xl sm:text-2xl" />
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <button onClick={handleGetStarted} className="hidden sm:block text-sm font-medium transition-colors hover:text-slate-900" style={{ color: '#475569' }} data-testid="nav-demo">Request Demo</button>
            <button onClick={() => navigate('/login')} className="text-sm font-medium whitespace-nowrap transition-colors hover:text-slate-900" style={{ color: '#475569' }} data-testid="nav-login">Sign In</button>
            <button onClick={handleGetStarted} className="px-4 sm:px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap text-white transition-all duration-300 hover:shadow-lg" style={{ background: BLUE }} data-testid="nav-get-started"
              onMouseEnter={e => e.currentTarget.style.background = NAVY} onMouseLeave={e => e.currentTarget.style.background = BLUE}>
              Get Started <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="pt-28 pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 50% at 70% 20%, rgba(0,180,216,0.07), transparent), radial-gradient(ellipse 50% 40% at 15% 80%, rgba(255,153,51,0.05), transparent)' }} />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium mb-8" style={{ background: 'rgba(0,180,216,0.08)', color: '#0E7490', border: '1px solid rgba(0,180,216,0.25)' }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: GREEN }} /> 302,000+ pre-enriched AU and NZ entities
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold leading-[1.05] tracking-tight" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }} data-testid="hero-tagline">
              Run your entire MGA on <span style={{ color: BLUE }}>one platform</span>.
            </h1>
            <p className="mt-8 text-lg leading-relaxed max-w-xl" style={{ color: '#475569' }} data-testid="hero-subline">
              From broker email to bound policy to month-end close — underwriting, claims, renewals, compliance and finance in one source of truth. And it arrives already knowing Australian commercial risk, not waiting for your data.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={handleGetStarted} data-testid="hero-get-started"
                className="px-8 py-4 rounded-full text-sm font-bold text-white transition-all duration-300 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]" style={{ background: ORANGE }}>
                Request Demo <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
              <button onClick={() => setVideoOpen(true)} data-testid="hero-watch-demo"
                className="px-8 py-4 rounded-full text-sm font-semibold transition-all duration-300 hover:bg-slate-50 flex items-center gap-2" style={{ color: BLUE, border: `1.5px solid ${BLUE}` }}>
                <Play className="w-4 h-4" fill={BLUE} /> Watch the 2-minute demo
              </button>
            </div>
            <button onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })} data-testid="hero-demo"
              className="mt-5 text-sm font-medium transition-colors hover:text-slate-900 inline-flex items-center gap-1" style={{ color: '#475569' }}>
              Or see how a day runs inside underAIter <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" style={{ color: '#64748b' }}>
              <span><CheckCircle className="w-4 h-4 inline mr-1.5" style={{ color: GREEN }} />No credit card</span>
              <span><CheckCircle className="w-4 h-4 inline mr-1.5" style={{ color: GREEN }} />AU/NZ data sovereignty</span>
              <span><CheckCircle className="w-4 h-4 inline mr-1.5" style={{ color: GREEN }} />SOC 2 target</span>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="relative">
              <div className="absolute -inset-6 rounded-3xl opacity-20 blur-3xl" style={{ background: `radial-gradient(circle, ${TEAL} 0%, transparent 70%)` }} />
              <div className="relative"><ScoringDemo /></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Stats Bar ─── */}
      <section className="py-14 border-y" style={{ background: '#F8FAFC', borderColor: '#e2e8f0' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
          {[
            { value: <Counter end={liveStats.entities} />, label: 'Pre-enriched entities' },
            { value: <Counter end={liveStats.classes || 18} />, label: 'Insurance classes' },
            { value: liveStats.stp_measured_pct == null ? <span data-testid="landing-stp-pending">—</span> : <Counter end={liveStats.stp_measured_pct} suffix="%" />, label: liveStats.stp_measured_pct == null ? 'STP rate — measured from the first live quote' : 'Measured STP rate' },
            { value: <Counter prefix="<" end={5} suffix=" min" />, label: 'Email to decision' },
          ].map((stat) => (
            <Reveal key={stat.label}>
              <p className="text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'IBM Plex Sans'", color: BLUE }}>{stat.value}</p>
              <p className="text-xs mt-2 font-semibold tracking-[0.15em] uppercase" style={{ color: '#64748b' }}>{stat.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── A Day in the Life of an MGA ─── */}
      <section id="how-it-works" className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" data-testid="day-in-life-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase" style={{ color: ORANGE }}>New to MGAs?</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-3" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>
              A day in the life of an MGA
            </h2>
            <p className="mt-4 text-lg max-w-2xl" style={{ color: '#475569' }}>
              No insurance jargon — this is what actually happens inside underAIter.com, from a broker's email to a bound policy.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 relative">
            <div className="hidden lg:block absolute top-6 left-[10%] right-[10%] h-px" style={{ background: 'linear-gradient(90deg, transparent, #cbd5e1 12%, #cbd5e1 88%, transparent)' }} />
            {DAY_IN_LIFE.map((item, i) => (
              <Reveal key={item.n} delay={i * 90}>
                <div className="h-full flex flex-col rounded-2xl bg-white p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 relative" style={{ border: '1px solid #e2e8f0' }} data-testid={`day-in-life-step-${item.n}`}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold text-white shadow-md relative z-10" style={{ background: BLUE, fontFamily: "'IBM Plex Sans'" }}>{item.n}</div>
                  <h3 className="text-base font-bold mt-5" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>{item.title}</h3>
                  <p className="text-sm mt-3 leading-relaxed flex-1" style={{ color: '#64748b' }}>{item.desc}</p>
                  <div className="mt-5 pt-4 flex items-start gap-2" style={{ borderTop: '1px dashed #e2e8f0' }}>
                    <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#cbd5e1' }} />
                    <span className="text-xs leading-snug" style={{ color: '#94a3b8' }}>
                      Replaces: <span className="line-through">{item.replaces}</span>
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── What underAIter.com Replaces ─── */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase" style={{ color: TEAL }}>The consolidation</p>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-3" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>
              What underAIter.com replaces
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#475569' }}>Every tool an MGA currently uses. Gone.</p>
          </Reveal>
          <div className="mt-14 space-y-3">
            {REPLACEMENTS.map((r, i) => (
              <Reveal key={r.from} delay={i * 60}>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-6 py-4 px-6 rounded-2xl bg-white transition-all duration-300 hover:shadow-md" style={{ border: '1px solid #e2e8f0' }}>
                  <span className="text-sm flex items-center gap-2" style={{ color: '#94a3b8' }}>
                    <XCircle className="w-4 h-4 shrink-0" style={{ color: '#cbd5e1' }} />
                    <span className="line-through">{r.from}</span>
                  </span>
                  <ArrowRight className="w-4 h-4 hidden sm:block" style={{ color: TEAL }} />
                  <span className="text-sm font-semibold flex items-center gap-2" style={{ color: '#0f172a' }}>
                    <CheckCircle className="w-4 h-4 shrink-0" style={{ color: GREEN }} />
                    {r.to}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Features Grid ─── */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8" style={{ background: '#F8FAFC' }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-center" style={{ color: TEAL }}>Capabilities</p>
            <h2 className="text-4xl sm:text-5xl font-semibold text-center tracking-tight mt-3" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>
              Built for insurance. Powered by AI.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 70}>
                <div className="p-8 rounded-2xl bg-white h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1" style={{ border: '1px solid #e2e8f0' }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(0,180,216,0.08)' }}>
                    <f.icon className="w-6 h-6" style={{ color: TEAL }} />
                  </div>
                  <h3 className="text-base font-bold mb-3" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>{f.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#64748b' }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-sm font-semibold tracking-[0.15em] uppercase text-center" style={{ color: TEAL }}>The workflow</p>
            <h2 className="text-4xl sm:text-5xl font-semibold text-center tracking-tight mt-3" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>
              How it works
            </h2>
            <p className="text-center mt-4 text-lg" style={{ color: '#475569' }}>Four steps from submission to bound policy.</p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px" style={{ background: 'linear-gradient(90deg, transparent, #cbd5e1 15%, #cbd5e1 85%, transparent)' }} />
            {STEPS.map((item, i) => (
              <Reveal key={item.step} delay={i * 100}>
                <div className="text-center relative">
                  <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center text-lg font-bold text-white relative z-10 shadow-md" style={{ background: BLUE, fontFamily: "'IBM Plex Sans'" }}>{item.step}</div>
                  <h3 className="text-base font-bold mt-5" style={{ fontFamily: "'IBM Plex Sans'", color: '#0f172a' }}>{item.title}</h3>
                  <p className="text-sm mt-3 leading-relaxed px-2" style={{ color: '#64748b' }}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA (dark contrast) ─── */}
      <section className="py-24 sm:py-28 px-4 sm:px-6 lg:px-8" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <UnderaiterMark size="text-4xl sm:text-5xl" dark />
            <h2 className="text-3xl sm:text-4xl font-semibold mt-4 tracking-tight text-white" style={{ fontFamily: "'IBM Plex Sans'" }}>
              One person. One platform.<br />A complete MGA.
            </h2>
            <p className="mt-5 text-base" style={{ color: '#B0BEC5' }}>
              Run the whole agency — intake to close — on one platform, one source of truth, inside underAIter.com.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <button onClick={handleGetStarted} data-testid="cta-get-started"
                className="px-10 py-4 rounded-full text-sm font-bold text-white transition-all duration-300 hover:shadow-xl hover:scale-[1.02]" style={{ background: ORANGE }}>
                Request Demo <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
              <button onClick={() => navigate('/login')} data-testid="cta-sign-in"
                className="px-10 py-4 rounded-full text-sm font-semibold text-white transition-all duration-300 hover:bg-white/10" style={{ border: '1.5px solid rgba(255,255,255,0.3)' }}>
                Sign In
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 bg-white" style={{ borderTop: '1px solid #e2e8f0' }} data-testid="landing-footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoIcon size={28} />
            <div>
              <UnderaiterMark size="text-base" />
              <p className="text-[11px] mt-0.5" style={{ color: '#475569' }} data-testid="footer-tagline">{TAGLINE}</p>
            </div>
          </div>
          <p className="text-xs" style={{ color: '#64748b' }}>underAIter.com | Sydney, NSW Australia | contact@underaiter.com</p>
          <div className="flex items-center gap-2 text-xs" style={{ color: '#94a3b8' }}>
            <span>© {new Date().getFullYear()} Synaion Pty Ltd</span>
            <span aria-hidden="true">·</span>
            <span>Evolutionary Insurance Intelligence</span>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes demoHold { from { width: 0%; } to { width: 100%; } }
      `}</style>
    </div>
  );
}
